import {DepositoryType, useAccountQuery} from '@transactions/graphql';
import {ArrowRightLeft, Pencil, Star, StarOff, Trash2} from 'lucide-react';
import * as React from 'react';
import {
  NavLink,
  Outlet,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import {ACCOUNT_TYPE_OPTIONS} from 'src/AccountsView/accountFilter';
import {useStarredAccounts} from 'src/AccountsView/useStarredAccounts';
import {useBreadcrumb} from 'src/Breadcrumbs';
import {CardArt} from 'src/CardArt';
import {isCardProductId} from 'src/cardProductOptions';
import {AccountV2Icon} from 'src/icons/AccountV2Icon';
import {cn} from 'src/lib/utils';
import Empty from 'src/ListView/Empty';
import {logoUrl} from 'src/logoOptions';
import {useModals} from 'src/Modals';
import {PopoverMenu} from 'src/PopoverMenu';
import PopoverMenuItem from 'src/PopoverMenuItem';
import type {AccountFragment} from '@transactions/graphql';

export type DepositoryAccount = Extract<
  AccountFragment,
  {__typename: 'DepositoryAccount'}
>;

export type InvestmentAccount = Extract<
  AccountFragment,
  {__typename: 'InvestmentAccount'}
>;

export type AccountOutletContext = {
  account: DepositoryAccount | InvestmentAccount;
  /**
   * Refetch the layout's account query. Called by tabs after operations that
   * change Account.balance server-side so the header balance doesn't go stale.
   */
  refetchAccount: () => void;
};

/** Typed accessor for the context AccountDetail passes to its tab routes. */
export const useAccountOutletContext = () =>
  useOutletContext<AccountOutletContext>();

/**
 * Human-readable depository type label, from the same option list that
 * drives the accounts-list filter and row labels — one place to name a
 * type (a duplicated switch here shipped 'Unknown' for ledger).
 */
export const formatDepositoryType = (type: DepositoryType): string =>
  ACCOUNT_TYPE_OPTIONS.find((option) =>
    option.matches({__typename: 'DepositoryAccount', depositoryType: type}),
  )?.label ?? 'Unknown';

const tabClass = ({isActive}: {isActive: boolean}) =>
  cn(
    'shrink-0 whitespace-nowrap border-b-2 px-1 pb-2 text-sm font-medium no-underline transition',
    isActive
      ? 'border-primary text-foreground'
      : 'text-muted-foreground hover:text-foreground border-transparent',
  );

/**
 * Unified account detail view: a shared header (name, actions) and tab
 * navigation, with the active tab's content rendered via Outlet. Depository
 * accounts get Overview / Transactions / Reconcile / Benefits; investment
 * accounts get Overview / Transactions / Snapshots. The Plaid connection now
 * lives in a card on the Overview tab. Each tab loads its own data, so
 * nothing beyond the light account query is fetched up front.
 */
export const AccountDetail = () => {
  const params = useParams();
  const accountId = params.id || '';
  const navigate = useNavigate();
  const {
    deleteAccountModal,
    editAccountModal,
    editInvestmentAccountModal,
    migrateAccountTransactionsModal,
  } = useModals();

  const {
    data: accountData,
    loading: accountLoading,
    refetch: refetchAccount,
  } = useAccountQuery({
    variables: {id: accountId},
    skip: !accountId,
  });

  const account = accountData?.account;
  const {
    starredIds,
    loading: starredLoading,
    toggleStar,
  } = useStarredAccounts();

  // Shared key across the tab sub-routes so switching tabs updates this
  // crumb (and its href) in place instead of appending new entries.
  useBreadcrumb({
    key: `/accounts/${accountId}`,
    label: account?.name ?? 'Loading...',
    logo: account?.logo,
    fallback: [{label: 'Accounts', href: '/accounts'}],
  });

  if (!accountId) {
    return <Empty>No account selected</Empty>;
  }

  if (accountLoading && !accountData) {
    return <Empty>Loading account…</Empty>;
  }

  if (!account) {
    return <Empty>Account not found</Empty>;
  }

  const isInvestment = account.__typename === 'InvestmentAccount';
  const isCreditCard =
    account.__typename === 'DepositoryAccount' &&
    account.depositoryType === DepositoryType.CreditCard;

  const outletContext: AccountOutletContext = {
    account,
    refetchAccount: () => void refetchAccount(),
  };

  const handleEdit = () => {
    if (account.__typename === 'InvestmentAccount') {
      editInvestmentAccountModal.openModal({
        account,
        onUpdate: () => void refetchAccount(),
        // The account page it lives on is gone once deleted — leave for the
        // investments overview.
        onDelete: () => navigate('/investments'),
      });
    } else {
      editAccountModal.openModal({
        account,
        onUpdate: () => void refetchAccount(),
      });
    }
  };

  const handleDelete = () => {
    if (account.__typename !== 'DepositoryAccount') {
      return;
    }
    deleteAccountModal.openModal({
      account,
      onDeleted: () => navigate('/accounts'),
    });
  };

  const handleMigrateTransactions = () => {
    migrateAccountTransactionsModal.openModal({
      account,
      onMigrated: () => void refetchAccount(),
    });
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header. Account facts (type, balance, Plaid status) live on the
          Overview tab, keeping this shell short so tabs and content fit on
          mobile screens. */}
      <div className="flex items-center justify-between gap-4 p-4 pb-0 sm:p-8 sm:pb-0">
        <div className="flex min-w-0 items-center gap-3">
          {account.__typename === 'DepositoryAccount' &&
          account.depositoryType === DepositoryType.CreditCard &&
          isCardProductId(account.cardProduct) ? (
            // A card shows its face (~1.6:1 at the logo's 32px height).
            // Gated on the type, not merely on having a product: switching a
            // card to Checking hides the picker but keeps the stored slug (so
            // switching back restores the face), and a checking account must
            // not wear credit-card artwork in the meantime.
            <CardArt product={account.cardProduct} className="w-20" />
          ) : account.logo ? (
            <img
              src={logoUrl(account.logo)}
              alt=""
              className="size-8 shrink-0 rounded-md"
            />
          ) : (
            <AccountV2Icon account={account} className="size-8 shrink-0" />
          )}
          <h1 className="text-foreground m-0 truncate text-3xl">
            {account.name}
          </h1>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <PopoverMenu>
            <PopoverMenuItem iconLeft={<Pencil />} onSelect={handleEdit}>
              Edit account
            </PopoverMenuItem>
            {/* The Accounts list hides its star buttons on a phone; this
                is where a phone stars. */}
            {/* Hidden until the starred list loads: before then an
                already-starred account would offer Star. */}
            {!starredLoading && (
              <PopoverMenuItem
                iconLeft={
                  starredIds.includes(account._id) ? <StarOff /> : <Star />
                }
                onSelect={() => toggleStar(account._id)}
              >
                {starredIds.includes(account._id) ? 'Unstar' : 'Star'}
              </PopoverMenuItem>
            )}
            {/* Not gated on transactionCount: that count excludes
                soft-deleted rows, which migrate too — the modal itself
                reports when there is truly nothing to move. */}
            <PopoverMenuItem
              iconLeft={<ArrowRightLeft />}
              onSelect={handleMigrateTransactions}
            >
              Migrate transactions
            </PopoverMenuItem>
            {/* Investment accounts delete from inside their edit modal. */}
            {!isInvestment && (
              <PopoverMenuItem
                iconLeft={<Trash2 />}
                onSelect={handleDelete}
                disabled={account.transactionCount > 0}
                title={
                  account.transactionCount > 0
                    ? 'Accounts with transactions cannot be deleted'
                    : undefined
                }
                className="text-destructive"
              >
                Delete account
              </PopoverMenuItem>
            )}
          </PopoverMenu>
        </div>
      </div>

      {/* Tabs. Horizontally scrollable so the full set stays reachable on
          narrow screens instead of overflowing the viewport. */}
      <nav className="border-border flex gap-5 overflow-x-auto border-b px-4 pt-4 [scrollbar-width:none] sm:px-8">
        <NavLink to={`/accounts/${account._id}`} end className={tabClass}>
          Overview
        </NavLink>
        <NavLink to={`/accounts/${account._id}/transactions`} className={tabClass}>
          Transactions
        </NavLink>
        {!isInvestment && (
          <NavLink to={`/accounts/${account._id}/reconcile`} className={tabClass}>
            Reconcile
          </NavLink>
        )}
        {isInvestment && (
          <NavLink to={`/accounts/${account._id}/snapshots`} className={tabClass}>
            Snapshots
          </NavLink>
        )}
        {isCreditCard && (
          <NavLink to={`/accounts/${account._id}/benefits`} className={tabClass}>
            Benefits
          </NavLink>
        )}
      </nav>

      {/* Active tab content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <Outlet context={outletContext} />
      </div>
    </div>
  );
};
